import { Alert, Button, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import type { Application } from '../../data/types';
import { applicationPayment } from '../../domain/raise/raiseCalc';
import { date, dateTime, DEMO_TODAY, money } from '../../lib/format';
import { useTenantScope } from '../../store/selectors';
import DataTable, { type Column } from '../../ui/DataTable';
import { PageHeader, SectionCard, StatCard, StatGrid, StatusChip } from '../../ui/primitives';
import { PaymentStatusChip } from '../applications/ApplicationPaymentPanel';
import { applicationReadiness } from '../../domain/kyc/readiness';
import { wholesaleModeFor } from '../../domain/kyc/wholesale';
import { useRegistry } from '../../store/RegistryStore';
import ReadinessChip from '../common/ReadinessChip';
import { offerApplications, offerTotals } from './offerTotals';

export default function OfferDetailPage() {
  const { offerId } = useParams();
  const scope = useTenantScope();
  const { state } = useRegistry();
  const navigate = useNavigate();
  const offer = scope.offers.find((item) => item.id === offerId);
  if (!offer) return <Navigate to="/manager/offers" replace />;
  const unitClass = scope.classById.get(offer.classId);
  const totals = offerTotals(offer, scope.applications, unitClass);
  const rows = offerApplications(offer, scope.applications);
  const runs = scope.issueRuns.filter((run) => run.offerId === offer.id);
  const payment = (row: Application) => applicationPayment(row, unitClass);
  const submitted = (row: Application) => Boolean(row.submittedAt);

  const columns: Column<Application>[] = [
    {
      key: 'ref',
      label: 'ID',
      sortValue: (row) => row.reference,
      render: (row) => (
        <Typography variant="body2" noWrap>
          {row.reference}
        </Typography>
      ),
    },
    {
      key: 'received',
      label: 'Received',
      hideBelow: 'lg',
      sortValue: (row) => row.submittedAt ?? '',
      render: (row) => date(row.submittedAt),
    },
    {
      key: 'investor',
      label: 'Investor',
      sortValue: (row) => row.applicantName,
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 180 }}>
          {row.applicantName}
        </Typography>
      ),
    },
    {
      key: 'notPaid',
      label: 'Not paid',
      align: 'right',
      sortValue: (row) => payment(row).outstanding,
      render: (row) => (submitted(row) ? money(payment(row).outstanding) : '—'),
    },
    {
      key: 'paid',
      label: 'Paid',
      align: 'right',
      sortValue: (row) => payment(row).received,
      render: (row) => money(payment(row).received),
    },
    {
      key: 'initial',
      label: 'Total initial',
      align: 'right',
      hideBelow: 'xl',
      render: (row) => money(payment(row).payableNow),
    },
    {
      key: 'later',
      label: 'Payable later',
      align: 'right',
      hideBelow: 'xl',
      render: (row) => money(payment(row).payableLater),
    },
    { key: 'total', label: 'Total', align: 'right', render: (row) => money(row.amount) },
    {
      key: 'crn',
      label: 'BPAY CRN',
      hideBelow: 'xl',
      render: (row) => row.paymentInstruction?.crn ?? '—',
    },
    {
      key: 'verification',
      label: 'Investor checks',
      render: (row) => (
        <ReadinessChip
          readiness={applicationReadiness(
            row,
            scope.holderByEntityId.get(row.investmentEntityId),
            scope.kycCases,
            DEMO_TODAY,
            undefined,
            wholesaleModeFor(state, row.fundId),
          )}
        />
      ),
    },
    {
      key: 'payment',
      label: 'Payment',
      render: (row) => (submitted(row) ? <PaymentStatusChip status={payment(row).status} /> : '—'),
    },
    { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
  ];

  return (
    <>
      <PageHeader
        eyebrow={
          <RouterLink to="/manager/offers" style={{ color: 'inherit' }}>
            Offers
          </RouterLink>
        }
        title={offer.title}
        subtitle={
          <>
            <StatusChip status={offer.status} /> Closes {dateTime(offer.closesAt)} ·{' '}
            {offer.issueAtClose
              ? 'units issued together at financial close'
              : 'units issued as each application completes'}
          </>
        }
        actions={
          <>
            <Button component={RouterLink} to={`/investor/offers/${offer.id}`}>
              View as investor
            </Button>
            {offer.issueAtClose && (
              <Button
                variant="contained"
                component={RouterLink}
                to={`/manager/offers/${offer.id}/close`}
              >
                Financial close
              </Button>
            )}
          </>
        }
      />
      <StatGrid min={170}>
        <StatCard
          label="Applications"
          value={totals.applications}
          hint={`${totals.inProgress} in progress`}
        />
        <StatCard label="Total initial" value={money(totals.totalInitial)} />
        <StatCard label="Paid" value={money(totals.paid)} tone="success" />
        <StatCard label="Not paid" value={money(totals.notPaid)} tone="warning" />
        <StatCard label="Payable later" value={money(totals.payableLater)} />
        <StatCard
          label="AML/CTF referred"
          value={totals.amlReferred}
          tone={totals.amlReferred ? 'danger' : 'neutral'}
        />
      </StatGrid>
      {runs.map((run) => (
        <Alert
          key={run.id}
          severity={
            run.status === 'ISSUED' ? 'success' : run.status === 'REJECTED' ? 'error' : 'info'
          }
          sx={{ mt: 2 }}
          action={
            <Button component={RouterLink} to={`/manager/offers/${offer.id}/close`} size="small">
              Open
            </Button>
          }
        >
          {run.reference}: {run.lines.length} applicants, settlement {date(run.settlementDate)} ·{' '}
          {run.status === 'ISSUED' ? 'units issued' : run.status.toLowerCase().replace('_', ' ')}
        </Alert>
      ))}
      <SectionCard title="Applications" sx={{ mt: 3 }} noPadding>
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          defaultSort={{ key: 'received', direction: 'desc' }}
          onRowClick={(row) => navigate(`/manager/applications/${row.id}`)}
          emptyMessage="No applications yet"
        />
      </SectionCard>
    </>
  );
}
